import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Project, Client } from '../../types';
import { useAuth } from './AuthContext';
import { useRBAC } from './RBACContext';
import apiService from '../../utils/api.service';

interface ProjectContextType {
  projects: Project[];
  clients: Client[];
  isLoading: boolean;
  error: string | null;
  fetchProjects: () => Promise<void>;
  fetchClients: () => Promise<void>;
  getProject: (id: string) => Project | undefined;
  getClient: (id: string) => Client | undefined;
  createProject: (data: Partial<Project>) => Promise<void>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  createClient: (data: Partial<Client>) => Promise<void>;
  updateClient: (id: string, data: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProjects must be used within ProjectContextProvider');
  return context;
};

export const ProjectContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { isSuperAdmin } = useRBAC();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setError(null);
    try {
      const response = await apiService.listProjects();
      const raw = response.data || response;
      setProjects(Array.isArray(raw) ? raw : (raw?.projects || []));
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    setError(null);
    try {
      const response = await apiService.listClients();
      const raw = response.data || response;
      setClients(Array.isArray(raw) ? raw : (raw?.clients || []));
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isSuperAdmin) {
      setIsLoading(true);
      Promise.all([fetchProjects(), fetchClients()])
        .finally(() => setIsLoading(false));
    } else {
      setProjects([]);
      setClients([]);
    }
  }, [isAuthenticated, isSuperAdmin, fetchProjects, fetchClients]);

  const getProject = useCallback((id: string): Project | undefined => {
    return projects.find(p => p.id === id);
  }, [projects]);

  const getClient = useCallback((id: string): Client | undefined => {
    return clients.find(c => c.id === id);
  }, [clients]);

  const createProject = useCallback(async (data: Partial<Project>) => {
    try {
      await apiService.createProject(data);
      await fetchProjects();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [fetchProjects]);

  const updateProject = useCallback(async (id: string, data: Partial<Project>) => {
    try {
      await apiService.updateProject(id, data);
      await fetchProjects();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [fetchProjects]);

  const deleteProject = useCallback(async (id: string) => {
    try {
      await apiService.deleteProject(id);
      await fetchProjects();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [fetchProjects]);

  const createClient = useCallback(async (data: Partial<Client>) => {
    try {
      await apiService.createClient(data);
      await fetchClients();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [fetchClients]);

  const updateClient = useCallback(async (id: string, data: Partial<Client>) => {
    try {
      await apiService.updateClient(id, data);
      await fetchClients();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [fetchClients]);

  const deleteClient = useCallback(async (id: string) => {
    try {
      await apiService.deleteClient(id);
      await fetchClients();
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [fetchClients]);

  return (
    <ProjectContext.Provider value={{
      projects, clients, isLoading, error,
      fetchProjects, fetchClients,
      getProject, getClient,
      createProject, updateProject, deleteProject,
      createClient, updateClient, deleteClient,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};
